// @flow
import { titleCaseToCamelCase } from 'utils/string';
import { getJson, putJson, postJson, del } from 'utils/http';
import type {
  AddressWithDescription,
  Admin,
  AdminRequest,
  AdminInvite
} from 'api/shared';
import { LISTING_TYPE_URL_SEGMENT_MAP } from 'utils/constants';
import type { Volop } from 'api/volopsAdmin';

export type Group = {
  id: string,
  name: string,
  description: string,
  locale: string,
  links: Array<string>,
  address: AddressWithDescription,
  url: ?{ [string]: string },
  isApproved: ?boolean,
  rejectionReason: ?string,
  isPublished: boolean,
  isFrozen: boolean,
  isSpam: boolean,
  canDeleteGroup: boolean,
  canAddAdmins: boolean,
  canAddOwners: boolean,
  created: number,
  admins: Array<Admin>,
  adminRequests: Array<AdminRequest>,
  invites: Array<AdminInvite>,
  searchApiKey: string,
  frozenListings: Array<{
    id: string,
    name: string,
    type: string
  }>
};

export type EventStatus =
  | 'ARCHIVED'
  | 'DELETED'
  | 'DRAFT'
  | 'FROZEN'
  | 'HIDDEN'
  | 'PUBLISHED'
  | 'PULLED';

export type IdealistDayEvent = {
  id: string,
  status: $ReadOnlyArray<EventStatus>,
  name: string,
  description: string,
  slug: string,
  starts: number,
  localizedStarts: string,
  date: string,
  ends: string,
  localizedEnds: string,
  timezone: string,
  creator: {
    id: string,
    name: string,
    email: string
  },
  address: AddressWithDescription,
  url: ?{ [string]: string },
  rsvpEmail: ?string,
  rsvpUrl: ?string,
  rsvpText: ?string,
  directions: ?string,
  isPublished: boolean,
  firstPublished: ?string,
  isDraft: boolean,
  isArchived: boolean,
  archived: ?string,
  isFrozen: boolean,
  isHidden: boolean,
  isEditable: boolean,
  canBeHidden: boolean,
  canBeUnhidden: boolean,
  canBeReposted: boolean,
  canBePosted: boolean,
  repostFrom: ?{
    id: string,
    name: string
  },
  created: number,
  updated: number,
  deleted: ?number,
  isApproved: null,
  type: 'IDEALIST_DAY_EVENT'
};

export type RepostedListing = {
  id: string,
  name: string,
  type: 'VOLOP',
  firstPublished: string,
  location: string
};

export function fetchGroup(groupId: string): Promise<Group> {
  return getJson(`/data/groupadmin/${groupId}`).then(({ group }) => group);
}

export function updateGroup(groupInfo: Object): Promise<Group> {
  return putJson(`/data/groupadmin/${groupInfo.id}`, groupInfo).then(
    ({ group }) => group
  );
}

export function deleteGroup(groupId: string) {
  return del(`/data/groupadmin/${groupId}`);
}

export function fetchEvent(
  groupId: string,
  eventId: string
): Promise<IdealistDayEvent> {
  return getJson(
    `/data/groupadmin/${groupId}/idealist-day-events/${eventId}`
  ).then(({ idealistDayEvent }) => idealistDayEvent);
}

export function createEvent(
  groupId: string,
  eventInfo: Object
): Promise<IdealistDayEvent> {
  return postJson(
    `/data/groupadmin/${groupId}/idealist-day-events`,
    eventInfo
  ).then(({ idealistDayEvent }) => idealistDayEvent);
}

export function updateEvent(
  groupId: string,
  eventId: string,
  eventInfo: Object
): Promise<IdealistDayEvent> {
  return putJson(
    `/data/groupadmin/${groupId}/idealist-day-events/${eventId}`,
    eventInfo
  ).then(({ idealistDayEvent }) => idealistDayEvent);
}

export function publishEvent(groupId: string, eventId: string) {
  return postJson(
    `/data/groupadmin/${groupId}/idealist-day-events/${eventId}/post`
  ).then(({ idealistDayEvent }) => idealistDayEvent);
}

export function hideEvent(groupId: string, eventId: string) {
  return postJson(
    `/data/groupadmin/${groupId}/idealist-day-events/${eventId}/hide`
  ).then(({ idealistDayEvent }) => idealistDayEvent);
}

export function unhideEvent(groupId: string, eventId: string) {
  return postJson(
    `/data/groupadmin/${groupId}/idealist-day-events/${eventId}/unhide`
  ).then(({ idealistDayEvent }) => idealistDayEvent);
}

export function deleteEvent(groupId: string, eventId: string) {
  return del(`/data/groupadmin/${groupId}/idealist-day-events/${eventId}`).then(
    ({ idealistDayEvent }) => idealistDayEvent
  );
}

export function fetchGroupEvents(
  groupId: string
): Promise<Array<IdealistDayEvent>> {
  return getJson(`/data/groupadmin/${groupId}/idealist-day-events`).then(
    ({ events }) => events
  );
}

export function addOwner(
  groupId: string,
  email: string
): Promise<{ user: ?Admin, invite: ?AdminInvite }> {
  return postJson(`/data/groupadmin/${groupId}/owners`, { email });
}

export function addAdmin(
  groupId: string,
  email: string
): Promise<{ user: ?Admin, invite: ?AdminInvite }> {
  return postJson(`/data/groupadmin/${groupId}/admins`, { email });
}

export function removeAdmin(groupId: string, userId: string) {
  return del(`/data/groupadmin/${groupId}/admins/${userId}`);
}

export function approveAdminRequest(groupId: string, requestId: string) {
  return postJson(`/data/groupadmin/${groupId}/admin-requests/${requestId}`, {
    approved: true
  }).then(({ admin }) => admin);
}

export function rejectAdminRequest(groupId: string, requestId: string) {
  return postJson(`/data/groupadmin/${groupId}/admin-requests/${requestId}`, {
    approved: false
  });
}

export function createVolop(
  groupId: string,
  payload: Object
): Promise<{ volop: Volop }> {
  return postJson(`/data/groupadmin/${groupId}/volops`, payload);
}

export function validateVolop(
  groupId: string,
  payload: Object
): Promise<{ volop: Volop }> {
  return postJson(`/data/groupadmin/${groupId}/volops-validation`, payload);
}

export function updateVolop(
  groupId: string,
  payload: Object
): Promise<{ volop: Volop }> {
  return putJson(`/data/groupadmin/${groupId}/volops/${payload.id}`, payload);
}

export function fetchListing(
  groupId: string,
  listingType: string,
  listingId: string
): Promise<Volop | IdealistDayEvent> {
  return getJson(
    `/data/groupadmin/${groupId}/${
      LISTING_TYPE_URL_SEGMENT_MAP[listingType]
    }/${listingId}`
  ).then(response => response[titleCaseToCamelCase(listingType)]);
}

export function hideListing({
  groupId,
  listingType,
  listingId
}: {
  groupId: string,
  listingType: string,
  listingId: string
}) {
  return postJson(
    `/data/groupadmin/${groupId}/${
      LISTING_TYPE_URL_SEGMENT_MAP[listingType]
    }/${listingId}/hide`
  ).then(response => response[titleCaseToCamelCase(listingType)]);
}

export function unhideListing({
  groupId,
  listingType,
  listingId
}: {
  groupId: string,
  listingType: string,
  listingId: string
}) {
  return postJson(
    `/data/groupadmin/${groupId}/${
      LISTING_TYPE_URL_SEGMENT_MAP[listingType]
    }/${listingId}/unhide`
  ).then(response => response[titleCaseToCamelCase(listingType)]);
}

export function publishListing({
  groupId,
  listingType,
  listingId
}: {
  groupId: string,
  listingType: string,
  listingId: string
}) {
  return postJson(
    `/data/groupadmin/${groupId}/${
      LISTING_TYPE_URL_SEGMENT_MAP[listingType]
    }/${listingId}/post`
  ).then(response => ({
    listing: response[titleCaseToCamelCase(listingType)],
    requiresPayment: response.requiresPayment,
    hasInvoice: response.hasInvoice
  }));
}

export function deleteListing({
  groupId,
  listingType,
  listingId
}: {
  groupId: string,
  listingType: string,
  listingId: string
}) {
  return del(
    `/data/groupadmin/${groupId}/${
      LISTING_TYPE_URL_SEGMENT_MAP[listingType]
    }/${listingId}`
  ).then(response => response[titleCaseToCamelCase(listingType)]);
}

export function fetchRepostedListings(
  groupId: string
): Promise<Array<RepostedListing>> {
  return getJson(`/data/groupadmin/${groupId}/published-reposts`).then(
    ({ listings }) => listings
  );
}
